import { FileUploadField } from '@/components/form-components/FileUploadField'
import { Button } from '@/components/shadcn/button'
import { Form } from '@/components/shadcn/form'
import { Sheet, SheetContent } from '@/components/shadcn/sheet'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { queryClient, RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { ENDPOINTS } from '@/util'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconFileOutline } from '@icons/IconFileOutline'
import { IconV2DownloadOutline } from '@icons/IconV2DownloadOutline'
import { Suspense, type ReactNode, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useListLandingFiles } from './useListLandingFiles'
import {
  buildIndexedUploadFormData,
  groupLandingFilesByPlaceholder,
  normalizeUploadFilesAction,
  type GroupedLandingFiles,
  type IndexedUploadFile,
  type NormalizedUploadFilePlaceholder,
  type UploadFilesActionConfig,
} from './upload-files'
import { resolveUploadFilesPermissions, type UploadFilesPermissions } from './upload-files-types'

type UploadFilesFormValues = {
  files: File | File[] | null
  placeholderFiles: Array<File | null>
}

export type UploadFilesActionTrigger = ReactNode | ((onOpen: () => void) => ReactNode)

export interface UploadFilesActionPanelProps {
  action: UploadFilesActionConfig
  title: string
  uploadNamespace: string
  trigger: UploadFilesActionTrigger
  permissions?: UploadFilesPermissions | null
  showLandingFiles?: boolean
  namespaceMappingPresent?: boolean
  showTitle?: boolean
}

export function UploadFilesActionPanel({
  action,
  title,
  uploadNamespace,
  trigger,
  permissions,
  showLandingFiles = true,
  namespaceMappingPresent = true,
  showTitle = true,
}: UploadFilesActionPanelProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const normalizedAction = normalizeUploadFilesAction(action)
  const triggerContent = typeof trigger === 'function' ? trigger(() => setSheetOpen(true)) : trigger

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      {triggerContent}
      <SheetContent className="!pointer-events-auto space-y-5 pr-0 lg:max-w-3xl">
        {showTitle && (
          <div className="px-4 pt-4">
            <h2 className="text-lg font-semibold">{title}</h2>
          </div>
        )}
        {showLandingFiles ? (
          <Suspense fallback={<div>Loading...</div>}>
            <UploadFilesActionContent
              action={normalizedAction}
              uploadNamespace={uploadNamespace}
              permissions={permissions}
              namespaceMappingPresent={namespaceMappingPresent}
              onClose={() => setSheetOpen(false)}
            />
          </Suspense>
        ) : (
          <UploadFilesActionForm
            action={normalizedAction}
            uploadNamespace={uploadNamespace}
            permissions={permissions}
            namespaceMappingPresent={namespaceMappingPresent}
            onClose={() => setSheetOpen(false)}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}

type UploadFilesActionFormProps = {
  action: ReturnType<typeof normalizeUploadFilesAction>
  uploadNamespace: string
  permissions?: UploadFilesPermissions | null
  namespaceMappingPresent: boolean
  existingFiles?: GroupedLandingFiles<LandingFile, NormalizedUploadFilePlaceholder>
  onClose: () => void
}

type UploadFilesActionContentProps = Omit<UploadFilesActionFormProps, 'existingFiles'>

type LandingFile = RestSchemas['Messages.Storage.ListLandingFileItem']

function UploadFilesActionContent({
  action,
  uploadNamespace,
  permissions,
  namespaceMappingPresent,
  onClose,
}: UploadFilesActionContentProps) {
  const { data: landingFiles } = useListLandingFiles(uploadNamespace)
  const files = landingFiles.files ?? []
  const resolvedPermissions = resolveUploadFilesPermissions(permissions)
  const groupedFiles = groupLandingFilesByPlaceholder(files, action.placeholders)

  if (action.placeholders.length > 0) {
    return (
      <UploadFilesActionForm
        action={action}
        uploadNamespace={uploadNamespace}
        permissions={resolvedPermissions}
        namespaceMappingPresent={namespaceMappingPresent}
        existingFiles={groupedFiles}
        onClose={onClose}
      />
    )
  }

  return (
    <>
      <LandingFilesListContent files={files} permissions={resolvedPermissions} />
      <UploadFilesActionForm
        action={action}
        uploadNamespace={uploadNamespace}
        permissions={resolvedPermissions}
        namespaceMappingPresent={namespaceMappingPresent}
        onClose={onClose}
      />
    </>
  )
}

function getFiles(value: UploadFilesFormValues['files']): File[] {
  if (Array.isArray(value)) {
    return value.filter((file): file is File => file instanceof File)
  }
  return value instanceof File ? [value] : []
}

function getDefaultFormValues(action: ReturnType<typeof normalizeUploadFilesAction>): UploadFilesFormValues {
  return {
    files: action.allowMultiple ? [] : null,
    placeholderFiles: action.placeholders.map(() => null),
  }
}

function UploadFilesActionForm({
  action,
  uploadNamespace,
  permissions,
  namespaceMappingPresent,
  existingFiles,
  onClose,
}: UploadFilesActionFormProps) {
  const { postStorageApiUploadLandingFiles } = useRestClient()
  const { appId, projectId, appKey, organisationId } = useAppContext()
  const resolvedPermissions = resolveUploadFilesPermissions(permissions)
  const defaultValues = getDefaultFormValues(action)
  const form = useForm<UploadFilesFormValues>({ defaultValues })

  const { mutate: uploadFiles, isPending: isUploading } = postStorageApiUploadLandingFiles.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['list-landing-files', appKey, projectId, organisationId, uploadNamespace],
      })
      toast.success('File upload completed')
      form.reset(defaultValues)
      onClose()
    },
    onError: (error) => {
      console.error('Error uploading file(s):', error)
      toast.error('File upload failed')
    },
  })

  const selectedFiles = form.watch('files')
  const selectedPlaceholderFiles = form.watch('placeholderFiles')
  const fileCount =
    action.placeholders.length > 0
      ? selectedPlaceholderFiles.filter((file): file is File => file instanceof File).length
      : getFiles(selectedFiles).length

  const onSubmit = (formValues: UploadFilesFormValues) => {
    const indexedFiles: IndexedUploadFile[] = []

    if (action.placeholders.length > 0) {
      formValues.placeholderFiles.forEach((file, index) => {
        const placeholder = action.placeholders[index]
        if (file && placeholder) {
          indexedFiles.push({ file, placeholder: placeholder.name })
        }
      })
    } else {
      getFiles(formValues.files).forEach((file) => indexedFiles.push({ file }))
    }

    if (!indexedFiles.length) {
      toast.error('Please select at least one file')
      return
    }
    if (!appId || !projectId || appId < 0 || projectId < 0 || !appKey) {
      toast.error('Missing app or project context for file upload')
      return
    }
    if (!namespaceMappingPresent) {
      toast.error('Missing namespace mapping for file upload')
      return
    }

    const body = buildIndexedUploadFormData({
      projectId,
      appKey,
      namespace: uploadNamespace,
      files: indexedFiles,
    })
    uploadFiles({ body })
  }

  if (!resolvedPermissions.canUpload && !existingFiles) {
    return null
  }

  return (
    <Form {...form}>
      <form className="space-y-6 p-4 pr-10" onSubmit={form.handleSubmit(onSubmit)}>
        {action.placeholders.length > 0 ? (
          <div className="space-y-4">
            {existingFiles && (
              <div className="rounded-t-md bg-muted px-5 py-1">
                <h2 className="font-bold">Uploaded files by placeholder</h2>
              </div>
            )}
            {action.placeholders.map((placeholder, index) => (
              <div key={`${placeholder.name}-${index}`} className="rounded-md border p-3">
                <h3 className="text-center font-bold">{placeholder.name}</h3>
                {placeholder.description && (
                  <p className="mt-1 text-center text-xs text-muted-foreground">{placeholder.description}</p>
                )}
                {existingFiles && (
                  <div className="mt-3 border-t bg-background p-3">
                    <h4 className="text-sm font-semibold">Uploaded files</h4>
                    {existingFiles.placeholderFiles[index]?.files.length ? (
                      <LandingFileItems
                        files={existingFiles.placeholderFiles[index]?.files ?? []}
                        permissions={resolvedPermissions}
                      />
                    ) : (
                      <p className="mt-2 text-sm text-muted-foreground">No files uploaded yet.</p>
                    )}
                  </div>
                )}
                {resolvedPermissions.canUpload && (
                  <div className="mt-4 border-t pt-3">
                    <FileUploadField
                      control={form.control}
                      name={`placeholderFiles.${index}` as `placeholderFiles.${number}`}
                      label="Add file"
                      acceptedFormats={placeholder.allowedFileExtensions}
                      multiple={false}
                      maxSize={placeholder.maxFileSize}
                      maxFiles={1}
                    />
                  </div>
                )}
              </div>
            ))}
            {existingFiles && existingFiles.unassignedFiles.length > 0 && (
              <section className="rounded-md border p-3">
                <h3 className="font-medium">Unassigned files</h3>
                <LandingFileItems files={existingFiles.unassignedFiles} permissions={resolvedPermissions} />
              </section>
            )}
          </div>
        ) : (
          <FileUploadField
            control={form.control}
            name="files"
            label={action.allowMultiple ? 'Upload files' : 'Upload file'}
            acceptedFormats={action.allowedFileExtensions}
            multiple={action.allowMultiple}
            maxSize={action.maxFileSize}
            maxFiles={action.maxFiles}
          />
        )}
        {resolvedPermissions.canUpload && (
          <div className="form-action-container">
            <Button type="submit" disabled={!fileCount || isUploading}>
              {isUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}

type LandingFilesListProps = {
  uploadNamespace: string
  permissions?: UploadFilesPermissions | null
  placeholders?: readonly NormalizedUploadFilePlaceholder[]
}

export function LandingFilesList({ uploadNamespace, permissions, placeholders = [] }: LandingFilesListProps) {
  const resolvedPermissions = resolveUploadFilesPermissions(permissions)
  const { data: landingFiles } = useListLandingFiles(uploadNamespace)
  if (!landingFiles) return null

  const files = landingFiles.files ?? []
  return <LandingFilesListContent files={files} permissions={resolvedPermissions} placeholders={placeholders} />
}

type LandingFilesListContentProps = {
  files: LandingFile[]
  permissions: UploadFilesPermissions
  placeholders?: readonly NormalizedUploadFilePlaceholder[]
}

function LandingFilesListContent({ files, permissions, placeholders = [] }: LandingFilesListContentProps) {
  const groupedFiles = groupLandingFilesByPlaceholder(files, placeholders)
  const placeholderMode = placeholders.length > 0

  return (
    <div className="mr-5 px-5">
      <div className="mt-10 rounded-md shadow">
        <div className="rounded-t-md bg-muted px-5 py-1">
          <h2 className="font-bold">
            {placeholderMode ? 'Uploaded files by placeholder' : 'Currently uploaded files:'}
          </h2>
        </div>
        <div className="rounded-b-md bg-white px-5 py-5">
          {placeholderMode ? (
            <div className="space-y-4">
              {groupedFiles.placeholderFiles.map(({ placeholder, files: placeholderFiles }, index) => (
                <section key={`${placeholder.name}-${index}`} className="rounded-md border p-3">
                  <h3 className="font-medium">{placeholder.name}</h3>
                  {placeholder.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{placeholder.description}</p>
                  )}
                  <div className="mt-3 border-t pt-3">
                    <h4 className="text-sm font-semibold">Uploaded files</h4>
                    {placeholderFiles.length ? (
                      <LandingFileItems files={placeholderFiles} permissions={permissions} />
                    ) : (
                      <p className="mt-2 text-sm text-muted-foreground">No files uploaded yet.</p>
                    )}
                  </div>
                </section>
              ))}
              {groupedFiles.unassignedFiles.length > 0 && (
                <section className="rounded-md border p-3">
                  <h3 className="font-medium">Unassigned files</h3>
                  <LandingFileItems files={groupedFiles.unassignedFiles} permissions={permissions} />
                </section>
              )}
            </div>
          ) : (
            <LandingFileItems files={files} permissions={permissions} />
          )}
        </div>
      </div>
    </div>
  )
}

type LandingFileItemsProps = {
  files: LandingFile[]
  permissions: UploadFilesPermissions
}

function LandingFileItems({ files, permissions }: LandingFileItemsProps) {
  return (
    <ul className="space-y-2">
      {files.map((file, index) => (
        <LandingFileItem
          key={`${file.blobName ?? file.fileName ?? 'file'}-${index}`}
          file={file}
          permissions={permissions}
        />
      ))}
    </ul>
  )
}

type LandingFileItemProps = {
  file: LandingFile
  permissions: UploadFilesPermissions
}

export function LandingFileItem({ file, permissions }: LandingFileItemProps) {
  const { deleteStorageApiDeleteLandingFile } = useRestClient()
  const { appKey, projectId, organisationId } = useAppContext()
  const [isDownloading, setIsDownloading] = useState(false)
  const { mutate: deleteFile, isPending: isDeleting } = deleteStorageApiDeleteLandingFile.useMutation(undefined)
  if (!appKey || !projectId || !organisationId) return null

  const onDelete = () => {
    deleteFile(
      {
        body: {
          appKey,
          projectId,
          organisationId,
          namespace: file.namespace ?? '',
          filename: file.fileName,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['list-landing-files', appKey, projectId, organisationId, file.namespace ?? ''],
          })
          toast.success('File deleted successfully')
        },
        onError: (error) => {
          console.error('Error deleting file:', error)
          toast.error('Failed to delete file')
        },
      }
    )
  }

  const onDownload = async () => {
    if (!file.encryptedBlobName) {
      toast.error('Missing file reference for download')
      return
    }

    if (!projectId || projectId < 0 || !appKey) {
      toast.error('Missing app or project context for file download')
      return
    }

    setIsDownloading(true)

    try {
      const token = JSON.parse(sessionStorage.getItem('token') ?? '')
      const params = new URLSearchParams()
      params.set('encryptedBlobName', file.encryptedBlobName)
      params.set('appKey', appKey)
      params.set('projectId', projectId.toString())

      const response = await fetch(`${ENDPOINTS.LANDING}/storage/download_landing_file/?${params.toString()}`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      })

      if (!response.ok) {
        let errorMessage = response.statusText || 'Unknown error'

        try {
          const responseJson = await response.json()
          if (responseJson.Message) {
            errorMessage = responseJson.Message
          }
        } catch {
          // Keep the status text when the error response is not JSON.
        }

        toast.error(`Error downloading file: ${errorMessage}`)
        return
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.fileName ?? 'download'
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
      toast.error('Error downloading file')
    } finally {
      setIsDownloading(false)
    }
  }

  const showActions = permissions.canDownload || permissions.canDelete

  return (
    <li className="flex items-center gap-x-2">
      <IconFileOutline className="size-4" />
      <span className="max-w-md truncate text-sm">{file.fileName}</span>
      {file.size && <span className="text-xs text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>}
      {showActions && (
        <div className="ml-auto flex items-center gap-1">
          {permissions.canDownload && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={isDownloading}
              aria-label="Download file"
              onClick={onDownload}
            >
              <IconV2DownloadOutline className="size-4" />
            </Button>
          )}
          {permissions.canDelete && (
            <ConfirmDialog
              trigger={
                <Button variant="ghost" size="icon" disabled={isDeleting} aria-label="Delete file">
                  <IconDeleteOutline className="size-4" />
                </Button>
              }
              title="Delete file"
              description="Are you sure you want to delete this file?"
              onConfirm={onDelete}
              isSubmitting={isDeleting}
            />
          )}
        </div>
      )}
    </li>
  )
}
